import React from "react";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import UnorderedListIcon from "@src/lib/icons/18px/unordered-list";
import { cn } from "@src/lib/utils/cn";
import {
  OPUS_AGENT_CARD_CLASS,
  OPUS_AGENT_CARD_INTERACTIVE_CLASS,
  OpusAgentCardAction,
  OpusAgentCardMedia,
  OpusAgentCardText,
} from "./OpusAgentCard";
import {
  type PlanProposalState,
  planItemCount,
  planStatusLabel,
} from "./planProposals";

interface OpusAgentPlanCardProps {
  state: PlanProposalState;
  onApprove: () => void;
  onView: () => void;
}

// The chat's pointer to a plan; the plan itself reads in the canvas. Only
// the plan that still needs an OK is a full card; proposed (a chat that
// can't send), approved and replaced plans fold to one row that
// still opens the canvas.
export const OpusAgentPlanCard: React.FC<OpusAgentPlanCardProps> = ({
  state,
  onApprove,
  onView,
}) => {
  const { plan } = state;
  const versionLabel = plan.version > 1 ? ` v${plan.version}` : "";
  const media = (
    <OpusAgentCardMedia count={plan.items.length} className="bg-bg-secondary">
      <UnorderedListIcon className="size-4 text-icon-secondary" />
    </OpusAgentCardMedia>
  );
  if (state.status !== "needsOk") {
    return (
      <button
        type="button"
        onClick={onView}
        data-testid="opus-agent-plan-card"
        data-plan-status={state.status}
        // The status is for screen readers only: on screen the chat around
        // the card already says it (the approval, the reply, the v2 below).
        aria-label={`View plan v${plan.version}, ${planStatusLabel(state)}`}
        className={cn(OPUS_AGENT_CARD_CLASS, OPUS_AGENT_CARD_INTERACTIVE_CLASS)}
      >
        {media}
        <OpusAgentCardText
          title={plan.title}
          subtitle={`Plan v${plan.version} · ${planItemCount(plan)}`}
        />
        <OpusAgentCardAction />
      </button>
    );
  }
  // The tiles beside "Plan v2" and the title, then the actions. Everything
  // else (kind, size, what changed) reads in the canvas.
  return (
    <div
      data-testid="opus-agent-plan-card"
      data-plan-status={state.status}
      className="flex w-full min-w-0 flex-col gap-2.5 rounded-2xl bg-bg-secondary p-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        {media}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-1.5">
            <Text
              variant="fine-print-description"
              className="min-w-0 truncate text-text-tertiary"
              data-testid="opus-agent-plan-heading"
            >
              Plan{versionLabel}
            </Text>
          </div>
          <Text
            variant="compact-title"
            className="block min-w-0 truncate text-text-primary"
            data-testid="opus-agent-plan-title"
            title={plan.title}
          >
            {plan.title}
          </Text>
        </div>
      </div>
      {/* Two equal halves, so the pair reads as one decision. */}
      <div
        className={cn(
          "grid min-w-0 gap-2",
          state.actionable ? "grid-cols-2" : "grid-cols-1",
        )}
      >
        {state.actionable && (
          <Button
            size="sm"
            onClick={onApprove}
            data-testid="opus-agent-plan-approve"
            className="w-full"
          >
            Approve &amp; build
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onView}
          data-testid="opus-agent-plan-view"
          className="w-full"
        >
          View plan
        </Button>
      </div>
    </div>
  );
};
